/**
 * Field definitions + payload helpers for the vault metrics introduced by
 * the Upload Health Screenshot flow (Jun 2026).
 *
 * Lives OUTSIDE `@/lib/LongevityEngine` — that file is a locked Engine and
 * must never be edited. These helpers extend the engine's payload shape
 * without replacing it: `buildBiometricPayload()` still builds the core
 * object, then `withHealthMetrics()` adds the new keys.
 */

export const HEALTH_EXTRA_FIELDS = Object.freeze([
  { key: "resting_heart_rate", label: "Resting Heart Rate", unit: "bpm", min: 20, max: 250 },
  { key: "daily_steps", label: "Daily Steps", unit: "steps", min: 0, max: 200000 },
]);

/** Blood markers a lab-report screenshot may show. Guests cannot write
 *  blood results (the server strips them), so these are carried as
 *  read-only additional data for the care team to verify. */
export const HEALTH_BLOOD_LABELS = Object.freeze({
  glucose: { label: "Fasting Glucose", unit: "mg/dL" },
  cholesterol: { label: "Cholesterol", unit: "mg/dL" },
  hba1c: { label: "HbA1c", unit: "%" },
  vitamin_d: { label: "Vitamin D", unit: "ng/mL" },
  ferritin: { label: "Ferritin", unit: "ng/mL" },
});

export function coerceHealthNumber(raw, { min, max } = {}) {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(String(raw).replace(",", "."));
  if (!Number.isFinite(n)) return null;
  if (typeof min === "number" && n < min) return null;
  if (typeof max === "number" && n > max) return null;
  return n;
}

/** Merge the screenshot-only metrics onto an engine-built payload. */
export function withHealthMetrics(payload, form) {
  const out = { ...(payload || {}) };
  for (const f of HEALTH_EXTRA_FIELDS) {
    const v = coerceHealthNumber(form?.[f.key], f);
    if (v !== null) out[f.key] = v;
  }
  const extra = {};
  for (const [k, v] of Object.entries(form?.extra_metrics || {})) {
    if (k && v !== null && v !== undefined && String(v).trim() !== "") {
      extra[String(k).slice(0, 60)] = String(v).slice(0, 60);
    }
  }
  if (Object.keys(extra).length) out.extra_metrics = extra;
  if (form?.source) out.source = form.source;
  return out;
}

/** At least one real measurement (core engine fields OR the new ones). */
export function healthPayloadHasData(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.body_composition && Object.keys(payload.body_composition).length) return true;
  if (typeof payload.vo2_max === "number") return true;
  if (typeof payload.sleep_score === "number") return true;
  if (typeof payload.resting_heart_rate === "number") return true;
  if (typeof payload.daily_steps === "number") return true;
  if (payload.blood_results && Object.keys(payload.blood_results).length) return true;
  if (payload.extra_metrics && Object.keys(payload.extra_metrics).length) return true;
  return false;
}
